/**
 * AdsBanner - Shows active marketing banners to users (styled with admin's colors)
 * Two variants: "card" (default, in dashboard) and "topbar" (full-width, every page)
 */
import { useState, useEffect, useCallback, useContext } from "react";
import axios from "axios";
import { X, ExternalLink, Bell } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AppContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function filterAdsByAudience(ads, role) {
  if (!role) return ads.filter(a => a.target_audience === "all" || !a.target_audience);
  if (role === "client") return ads.filter(a => !a.target_audience || a.target_audience === "all" || a.target_audience === "clients");
  if (role === "provider") return ads.filter(a => !a.target_audience || a.target_audience === "all" || a.target_audience === "providers");
  return ads; // admin sees all
}

// ── Global Top Bar (shown on every page) ───────────────────────────────────
export const GlobalAdTopBar = () => {
  const [ads, setAds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('dismissedAds') || '[]'); }
    catch { return []; }
  });
  const location = useLocation();
  const { user } = useAuth();

  const fetchAds = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ads/active`);
      setAds(res.data.ads || []);
    } catch {}
  }, []);

  useEffect(() => { fetchAds(); }, [fetchAds]);

  // Rotate through multiple ads every 6 seconds
  useEffect(() => {
    if (ads.length <= 1) return;
    const timer = setInterval(() => setCurrentIndex(i => (i + 1) % ads.length), 6000);
    return () => clearInterval(timer);
  }, [ads.length]);

  // Don't show on admin pages (after all hooks)
  const isAdmin = location.pathname.startsWith('/admin');
  const audienceFiltered = filterAdsByAudience(ads, user?.role);
  const activeAds = audienceFiltered.filter(a => !dismissed.includes(a.ad_id));
  if (isAdmin || !activeAds.length) return null;

  const ad = activeAds[currentIndex % activeAds.length];
  if (!ad) return null;

  const dismiss = () => {
    const next = [...dismissed, ad.ad_id];
    setDismissed(next);
    sessionStorage.setItem('dismissedAds', JSON.stringify(next));
  };

  const handleClick = async () => {
    try { await axios.post(`${API}/ads/${ad.ad_id}/click`); } catch {}
    if (ad.link_url) window.open(ad.link_url, '_blank', 'noopener');
  };

  return (
    <div
      style={{ backgroundColor: ad.background_color || '#dc2626', color: ad.text_color || '#ffffff' }}
      className="w-full flex items-center justify-between px-4 py-2 text-sm relative z-50 min-h-[40px]"
      data-testid="global-ad-topbar"
    >
      {/* Left spacer for balance */}
      <div className="w-6 flex-shrink-0" />

      {/* Content */}
      <div className="flex-1 flex items-center justify-center gap-2 min-w-0">
        <Bell className="w-4 h-4 flex-shrink-0 opacity-90" />
        <span className="truncate text-center text-xs sm:text-sm">
          {ad.description}{' '}
          {ad.sponsor_name && ad.link_url && (
            <button
              onClick={handleClick}
              className="font-bold underline hover:no-underline inline-flex items-center gap-1 cursor-pointer"
              data-testid="ad-topbar-link"
            >
              {ad.sponsor_name}
              <ExternalLink className="w-3 h-3" />
            </button>
          )}
        </span>
      </div>

      {/* Dismiss */}
      <button
        onClick={dismiss}
        className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-black/20 hover:bg-black/30 transition-colors ml-2"
        aria-label="Dismiss"
        data-testid="ad-topbar-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

// ── Dashboard Card Banner (existing style) ─────────────────────────────────
export const AdsBanner = ({ className = "" }) => {
  const [ads, setAds] = useState([]);
  const [dismissed, setDismissed] = useState([]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/ads/active`);
        setAds(res.data.ads || []);
      } catch {}
    };
    fetch();
  }, []);

  const activeAds = ads.filter(a => !dismissed.includes(a.ad_id));
  if (!activeAds.length) return null;

  const handleClick = async (ad) => {
    try { await axios.post(`${API}/ads/${ad.ad_id}/click`); } catch {}
    if (ad.link_url) window.open(ad.link_url, '_blank', 'noopener');
  };

  return (
    <div className={`space-y-3 ${className}`} data-testid="ads-banner">
      {activeAds.map(ad => (
        <div
          key={ad.ad_id}
          className="rounded-xl overflow-hidden relative group"
          style={{ backgroundColor: ad.background_color || '#2563eb' }}
          data-testid={`ad-${ad.ad_id}`}
        >
          <button
            className="absolute top-2 right-2 w-5 h-5 rounded-full bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
            onClick={() => setDismissed(d => [...d, ad.ad_id])}
            aria-label="Dismiss"
          >
            <X className="w-3 h-3" style={{ color: ad.text_color || '#fff' }} />
          </button>
          <div
            className="flex items-start gap-3 p-4 cursor-pointer"
            style={{ color: ad.text_color || '#ffffff' }}
            onClick={() => handleClick(ad)}
          >
            {ad.image_url ? (
              <img src={ad.image_url} alt={ad.title}
                className="w-10 h-10 object-contain rounded-lg bg-white/20 p-1 flex-shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0 text-base font-bold">
                {ad.title?.[0]?.toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm">{ad.title}</p>
              <p className="text-xs opacity-85 mt-0.5 line-clamp-3">{ad.description}</p>
              {ad.link_url && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold mt-2 opacity-90 hover:opacity-100 underline">
                  {ad.cta_text || 'Learn more'} <ExternalLink className="w-3 h-3" />
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AdsBanner;
